/**
 * Hotel Country Codes Thunk
 *
 * Fetches country codes
 * POST /hotels/country-codes
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { CountryCodesData } from '@contracts';
import {
  fetchHotelCountryCodes as fetchHotelCountryCodesApi,
  HotelCountryCodesError,
} from '../../api';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface FetchHotelCountryCodesParams {
  language?: string;
  region?: string;
  currency?: string;
}

interface FetchHotelCountryCodesResult {
  countryCodesData: CountryCodesData;
}

// ============================================
// Thunk
// ============================================

/**
 * Fetch country codes
 * POST /hotels/country-codes
 */
export const fetchHotelCountryCodesThunk = createAsyncThunk<
  FetchHotelCountryCodesResult,
  FetchHotelCountryCodesParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotels/fetchCountryCodes',
  async (_params, { rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const response = await fetchHotelCountryCodesApi({
        sessionId,
        userId: getUUID(),
      });

      return {
        countryCodesData: response.data,
      };
    } catch (error) {
      logger.error('[CountryCodesThunk] Failed to fetch country codes:', error);
      if (error instanceof HotelCountryCodesError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch country codes');
    }
  },
  {
    // Prevent duplicate API calls
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { isCountryCodesLoading } = state.hotels.bookingReview;
      return !isCountryCodesLoading;
    },
  },
);
